/*
  Nova cart upgrades
  - Rewards progress bar (free shipping tier + free gift tier)
  - Recently viewed strip inside the cart drawer
  - Groups recently viewed and the theme's cross-sell row at the bottom of the drawer
  - "Forgot item" reminder for the last product removed from the cart
  Config comes from sections/nova-cart-upgrades.liquid (#NovaCartConfig).
  Stays in step with the cart by reading the cart responses the page already
  receives (theme and apps) plus the theme's DOM events, so the bar updates in
  the same moment as the line items instead of waiting for an extra request.
*/
(function () {
  'use strict';

  var cfgEl = document.getElementById('NovaCartConfig');
  if (!cfgEl) return;
  var cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (err) {
    return;
  }

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var PAGE_ID = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  var KEY_SNAPSHOT = 'novaCartSnapshot';
  var KEY_FORGOT = 'novaCartForgot';
  var KEY_GIFT_DECLINED = 'novaGiftDeclined';
  var KEY_RECENT = 'recentlyViewed'; // written by the theme itself
  var GIFT_PROP = '_nova_gift';
  var DAY_MS = 86400000;

  var state = {
    cart: null,
    busy: false,
    ready: false,
    mounted: false,
    hadGift: false,
    hasCross: false,
    giftBlocked: false,
    syncTimer: null,
    reading: 0,
    readAgain: false,
    lastReadStart: 0,
    lastChangeAt: 0,
    signature: null,
    extrasSignature: null,
    products: {}
  };
  var els = {};

  /* ---------- helpers ---------- */

  function store(area) {
    return {
      get: function (key) {
        try {
          var raw = window[area].getItem(key);
          return raw ? JSON.parse(raw) : null;
        } catch (e) {
          return null;
        }
      },
      set: function (key, value) {
        try {
          window[area].setItem(key, JSON.stringify(value));
        } catch (e) {}
      },
      remove: function (key) {
        try {
          window[area].removeItem(key);
        } catch (e) {}
      }
    };
  }
  // Own requests use the untouched fetch so they are not read back by the tap.
  var nativeFetch = window.fetch;

  var local = store('localStorage');
  var session = store('sessionStorage');

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function withDelimiters(cents, precision, thousands, decimal) {
    var parts = (cents / 100).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, trimZeros) {
    var format = (window.theme && window.theme.moneyFormat) || cfg.moneyFormat || '{{amount}}';
    var out = format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(cents, 0, ' ', ',');
        case 'amount_with_space_separator':
          return withDelimiters(cents, 2, ' ', ',');
        default:
          return withDelimiters(cents, 2, ',', '.');
      }
    });
    out = out.replace(/<[^>]*>/g, '');
    if (trimZeros) out = out.replace(/[.,]00(?!\d)/, '');
    return out;
  }

  function fill(template, amount) {
    return String(template || '').replace('[amount]', amount);
  }

  function sizedImage(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function request(path, body) {
    var options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return nativeFetch.call(window, ROOT + path, options).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  function fetchCart() {
    return request('cart.js');
  }

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function fetchProduct(handle) {
    if (!state.products[handle]) {
      state.products[handle] = request('products/' + encodeURIComponent(handle) + '.js').catch(function () {
        return null;
      });
    }
    return state.products[handle];
  }

  // The theme listens for this event and re-renders the drawer from /cart.js.
  function refreshTheme() {
    document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
  }

  function isGift(item) {
    return !!(item.properties && item.properties[GIFT_PROP]);
  }

  function hasPaidItems(cart) {
    return cart.items.some(function (item) {
      return !isGift(item);
    });
  }

  // Cart value that counts towards the tiers: the gift line never counts.
  function eligibleTotal(cart) {
    var total = cart.total_price;
    cart.items.forEach(function (item) {
      if (isGift(item)) total -= item.final_line_price;
    });
    return Math.max(0, total);
  }

  function cartToken(cart) {
    return String(cart.token || '').split('?')[0];
  }

  /* ---------- mounting ---------- */

  function mount() {
    if (state.mounted && els.scroll && document.body.contains(els.scroll)) return true;
    var drawer = document.querySelector('[data-quick-cart]');
    var items = drawer && drawer.querySelector('.quick-cart__items');
    if (!items) return false;

    els.rewards = el('div', 'nova-rewards');
    els.rewards.hidden = true;
    els.message = el('p', 'nova-rewards__msg');
    els.message.setAttribute('aria-live', 'polite');
    els.track = el('div', 'nova-rewards__track');
    els.track.setAttribute('role', 'progressbar');
    els.track.setAttribute('aria-valuemin', '0');
    els.track.setAttribute('aria-valuemax', '100');
    els.fill = el('div', 'nova-rewards__fill');
    els.stops = el('div', 'nova-rewards__stops');
    els.labels = el('div', 'nova-rewards__labels');
    els.track.appendChild(els.fill);
    els.track.appendChild(els.stops);
    els.rewards.appendChild(els.message);
    els.rewards.appendChild(els.track);
    els.rewards.appendChild(els.labels);

    // One scroll area: line items on top, upsell block pinned to the bottom.
    els.scroll = el('div', 'nova-scroll');
    els.bottom = el('div', 'nova-bottom');
    els.extras = el('div', 'nova-extras');
    els.extras.hidden = true;
    els.cross = el('div', 'nova-cross');
    els.cross.hidden = true;
    els.bottom.appendChild(els.extras);
    els.bottom.appendChild(els.cross);

    items.parentNode.insertBefore(els.rewards, items);
    items.parentNode.insertBefore(els.scroll, items);
    els.scroll.appendChild(items);
    els.scroll.appendChild(els.bottom);
    mountCrossSell(drawer);

    // The theme re-renders the line items on every cart change, whichever
    // event it fires, so watching them keeps the bar in step with what is shown.
    els.items = items;
    if (window.MutationObserver) {
      new MutationObserver(reconcile).observe(items, { childList: true });
    }

    state.mounted = true;
    state.signature = null;
    state.extrasSignature = null;
    return true;
  }

  // Moves the theme's cross-sell row out of the fixed footer so it sits with
  // recently viewed at the bottom of the scroll area. The row itself is untouched.
  function mountCrossSell(drawer) {
    var list = drawer.querySelector('.quick-cart__footer .cross_selling_cart');
    if (!list) return;
    var original = list.previousElementSibling;
    if (original && original.classList.contains('promo_text')) original.style.display = 'none';
    if (cfg.t.crossHeading) els.cross.appendChild(el('p', 'nova-extras__heading', cfg.t.crossHeading));
    els.cross.appendChild(list);
    state.hasCross = true;
  }

  /* ---------- rewards progress bar ---------- */

  function giftConfigured() {
    return !!(cfg.features.progress && cfg.features.gift && cfg.gift.variantId && cfg.gift.threshold > 0);
  }

  // The gift tier is only promised while the gift can really be free.
  function giftActive() {
    return giftConfigured() && !state.giftBlocked;
  }

  // Milestones on the bar. Tiers with the same threshold share one stop, and
  // stops are evenly spaced so their labels never collide.
  function getStops() {
    var tiers = [];
    if (!cfg.features.progress) return tiers;
    if (cfg.shipping.alwaysFree || cfg.shipping.threshold === 0) {
      tiers.push({ type: 'shipping', amount: 0 });
    } else if (cfg.shipping.threshold > 0) {
      tiers.push({ type: 'shipping', amount: cfg.shipping.threshold });
    }
    if (giftActive()) tiers.push({ type: 'gift', amount: cfg.gift.threshold });
    tiers.sort(function (a, b) {
      return a.amount - b.amount;
    });

    var stops = [];
    tiers.forEach(function (tier) {
      var last = stops[stops.length - 1];
      if (last && last.amount === tier.amount) {
        last.types.push(tier.type);
      } else {
        stops.push({ amount: tier.amount, types: [tier.type] });
      }
    });

    var paid = stops.filter(function (stop) {
      return stop.amount > 0;
    }).length;
    var index = 0;
    stops.forEach(function (stop) {
      if (stop.amount === 0) {
        stop.position = paid ? 0 : 100;
        return;
      }
      index += 1;
      stop.position = (index / paid) * 100;
    });
    return stops;
  }

  function fillPercent(stops, total) {
    var previousAmount = 0;
    var previousPosition = 0;
    for (var i = 0; i < stops.length; i++) {
      var stop = stops[i];
      if (total < stop.amount) {
        var share = (total - previousAmount) / (stop.amount - previousAmount);
        return previousPosition + share * (stop.position - previousPosition);
      }
      previousAmount = stop.amount;
      previousPosition = stop.position;
    }
    return 100;
  }

  function stopLabel(stop) {
    return stop.types
      .map(function (type) {
        return type === 'gift' ? cfg.t.labelGift : cfg.t.labelShipping;
      })
      .join(' + ');
  }

  function renderRewards(cart) {
    var stops = getStops();
    if (!stops.length || !cart || !hasPaidItems(cart)) {
      els.rewards.hidden = true;
      return;
    }

    var total = eligibleTotal(cart);
    var percent = Math.round(fillPercent(stops, total));
    var next = null;
    var hasGift = false;
    stops.forEach(function (stop) {
      if (!next && total < stop.amount) next = stop;
      if (stop.types.indexOf('gift') > -1) hasGift = true;
    });

    var message;
    if (next) {
      var remaining = formatMoney(next.amount - total, true);
      var template = next.types.indexOf('shipping') > -1 ? cfg.t.shippingRemaining : cfg.t.giftRemaining;
      message = fill(template, remaining);
    } else {
      message = hasGift ? cfg.t.giftUnlocked : cfg.t.shippingUnlocked;
    }

    els.message.textContent = message;
    els.fill.style.width = percent + '%';
    els.track.setAttribute('aria-valuenow', String(percent));
    els.track.setAttribute('aria-valuetext', message);

    els.stops.textContent = '';
    els.labels.textContent = '';
    stops.forEach(function (stop) {
      var reached = total >= stop.amount;

      var marker = el('span', 'nova-rewards__stop' + (reached ? ' is-reached' : ''));
      marker.style.left = stop.position + '%';
      els.stops.appendChild(marker);

      var label = el('span', 'nova-rewards__label' + (reached ? ' is-reached' : ''), stopLabel(stop));
      if (stop.position >= 100) {
        label.style.right = '0';
      } else if (stop.position <= 0) {
        label.style.left = '0';
      } else {
        label.style.left = stop.position + '%';
        label.style.transform = 'translateX(-50%)';
      }
      els.labels.appendChild(label);
    });

    els.rewards.hidden = false;
  }

  /* ---------- free gift ---------- */

  function findGift(cart) {
    var line = null;
    cart.items.forEach(function (item) {
      if (isGift(item)) line = item;
    });
    return line;
  }

  // Reads sent while the gift was being added or removed are out of date.
  function giftDone() {
    state.busy = false;
    state.lastChangeAt = now();
    refreshTheme();
  }

  function removeGift(line) {
    state.busy = true;
    request('cart/change.js', { id: line.key, quantity: 0 })
      .catch(function () {})
      .then(giftDone);
  }

  function addGift() {
    var properties = {};
    properties[GIFT_PROP] = 'true';
    state.busy = true;
    request('cart/add.js', { items: [{ id: cfg.gift.variantId, quantity: 1, properties: properties }] })
      .then(fetchCart)
      .then(function (cart) {
        var line = findGift(cart);
        // Safety: never leave a "free" gift in the cart at a price.
        if (line && cfg.gift.requireFree && line.final_line_price > 0) {
          state.giftBlocked = true;
          if (window.console) {
            console.warn('[Nova cart] The gift is not free in the cart (no matching automatic discount), so the gift tier is off until the page is reloaded.');
          }
          return request('cart/change.js', { id: line.key, quantity: 0 });
        }
      })
      .catch(function () {
        state.giftBlocked = true;
      })
      .then(giftDone);
  }

  function syncGift(cart) {
    if (!giftConfigured() || state.busy) return;
    var line = findGift(cart);
    var eligible = hasPaidItems(cart) && eligibleTotal(cart) >= cfg.gift.threshold;

    if (!eligible) {
      session.remove(KEY_GIFT_DECLINED);
      if (line) removeGift(line);
      return;
    }
    if (line) {
      if (cfg.gift.requireFree && line.final_line_price > 0) {
        state.giftBlocked = true;
        removeGift(line);
      }
      return;
    }
    if (state.giftBlocked || session.get(KEY_GIFT_DECLINED)) return;
    if (state.hadGift) {
      // The shopper removed the gift themselves: respect that for this session.
      session.set(KEY_GIFT_DECLINED, 1);
      return;
    }
    addGift();
  }

  /* ---------- removal tracking (forgot item) ---------- */

  function trackable(item) {
    return !isGift(item) && item.final_price > 0 && cfg.excludeVariantIds.indexOf(item.variant_id) === -1;
  }

  function lineInfo(item) {
    return {
      variant_id: item.variant_id,
      product_id: item.product_id,
      handle: item.handle,
      title: item.product_title,
      variant_title: item.variant_title || '',
      image: item.image || '',
      price: item.final_price
    };
  }

  // flagRemovals is only true for in-page cart changes, so a cart that was
  // emptied by a completed order is never treated as "forgotten".
  function track(cart, flagRemovals) {
    var previous = local.get(KEY_SNAPSHOT);
    var token = cartToken(cart);
    var sameCart = !!(previous && previous.token === token);
    var inCart = {};
    cart.items.forEach(function (item) {
      inCart[item.variant_id] = true;
    });

    state.hadGift = !!(sameCart && previous.hadGift);

    if (flagRemovals && sameCart && Array.isArray(previous.items)) {
      var removed = previous.items.filter(function (line) {
        return !inCart[line.variant_id];
      });
      if (removed.length) {
        var last = removed[0];
        last.removedAt = Date.now();
        last.pageId = PAGE_ID;
        local.set(KEY_FORGOT, last);
      }
    }

    var forgot = local.get(KEY_FORGOT);
    if (forgot && inCart[forgot.variant_id]) local.remove(KEY_FORGOT);

    local.set(KEY_SNAPSHOT, {
      token: token,
      items: cart.items.filter(trackable).map(lineInfo),
      hadGift: cart.items.some(isGift)
    });
  }

  /* ---------- extras: forgot item + recently viewed ---------- */

  function currentForgot(inCartVariants) {
    if (!cfg.features.forgot) return null;
    var forgot = local.get(KEY_FORGOT);
    if (!forgot || !forgot.variant_id) return null;
    // Only remind on a later visit, not right after the removal.
    if (forgot.pageId === PAGE_ID) return null;
    if (Date.now() - forgot.removedAt > cfg.forgotDays * DAY_MS || inCartVariants[forgot.variant_id]) {
      local.remove(KEY_FORGOT);
      return null;
    }
    return forgot;
  }

  function productUrl(handle) {
    return ROOT + 'products/' + handle;
  }

  function buildForgot(forgot) {
    var wrap = el('div', 'nova-forgot');
    wrap.appendChild(el('p', 'nova-extras__heading', cfg.t.forgotHeading));

    var row = el('div', 'nova-card nova-card--wide');
    var imageLink = el('a', 'nova-card__image');
    imageLink.href = productUrl(forgot.handle);
    if (forgot.image) {
      var img = el('img');
      img.src = sizedImage(forgot.image, 160);
      img.alt = forgot.title || '';
      img.loading = 'lazy';
      img.width = 60;
      img.height = 60;
      imageLink.appendChild(img);
    }

    var info = el('div', 'nova-card__info');
    var title = el('a', 'nova-card__title', forgot.title);
    title.href = productUrl(forgot.handle);
    info.appendChild(title);
    if (forgot.variant_title && forgot.variant_title !== 'Default Title') {
      info.appendChild(el('span', 'nova-card__variant', forgot.variant_title));
    }
    var price = el('span', 'nova-card__price');
    info.appendChild(price);

    var action = el('div', 'nova-card__action');
    var add = el('button', 'nova-card__add nova-card__add--solid', cfg.t.forgotButton);
    add.type = 'button';
    action.appendChild(add);

    var dismiss = el('button', 'nova-card__dismiss', '×');
    dismiss.type = 'button';
    dismiss.setAttribute('aria-label', cfg.t.dismiss);

    function showViewLink() {
      action.textContent = '';
      var link = el('a', 'nova-card__add', cfg.t.viewProduct);
      link.href = productUrl(forgot.handle);
      action.appendChild(link);
    }

    add.addEventListener('click', function () {
      add.disabled = true;
      request('cart/add.js', { items: [{ id: forgot.variant_id, quantity: 1 }] })
        .then(function () {
          local.remove(KEY_FORGOT);
          refreshTheme();
        })
        .catch(showViewLink);
    });

    dismiss.addEventListener('click', function () {
      local.remove(KEY_FORGOT);
      renderExtras();
    });

    // Refresh price and availability for the current market.
    fetchProduct(forgot.handle).then(function (product) {
      if (!product) {
        local.remove(KEY_FORGOT);
        if (wrap.parentNode) renderExtras();
        return;
      }
      var variant = null;
      (product.variants || []).forEach(function (item) {
        if (item.id === forgot.variant_id) variant = item;
      });
      if (!variant) {
        showViewLink();
        return;
      }
      price.textContent = formatMoney(variant.price, true);
      if (!variant.available) showViewLink();
    });

    row.appendChild(imageLink);
    row.appendChild(info);
    row.appendChild(action);
    row.appendChild(dismiss);
    wrap.appendChild(row);
    return wrap;
  }

  function recentProducts(inCartProducts, forgot) {
    if (!cfg.features.recent) return [];
    var list = local.get(KEY_RECENT);
    if (!Array.isArray(list)) return [];
    return list
      .filter(function (product) {
        return (
          product &&
          product.id &&
          product.handle &&
          !inCartProducts[product.id] &&
          product.id !== cfg.currentProductId &&
          !(forgot && forgot.product_id === product.id)
        );
      })
      .slice(0, cfg.recentLimit || 6);
  }

  function buildRecent(products) {
    var wrap = el('div', 'nova-recent');
    wrap.appendChild(el('p', 'nova-extras__heading', cfg.t.recentHeading));
    var list = el('div', 'nova-row');

    products.forEach(function (product) {
      var card = el('div', 'nova-card');
      var imageLink = el('a', 'nova-card__image');
      imageLink.href = productUrl(product.handle);
      if (product.featured_image) {
        var img = el('img');
        img.src = sizedImage(product.featured_image, 160);
        img.alt = product.title || '';
        img.loading = 'lazy';
        img.width = 60;
        img.height = 60;
        imageLink.appendChild(img);
      }
      // Shared card layout: image, title and price, add button on the right.
      var info = el('div', 'nova-card__info');
      var title = el('a', 'nova-card__title', product.title);
      title.href = productUrl(product.handle);
      var price = el('span', 'nova-card__price');
      // Opens the theme's own quick-add modal (size selection).
      var add = el('button', 'nova-card__add', cfg.t.recentButton);
      add.type = 'button';
      add.setAttribute('data-quick-add', product.handle);

      info.appendChild(title);
      info.appendChild(price);
      card.appendChild(imageLink);
      card.appendChild(info);
      card.appendChild(add);
      list.appendChild(card);

      fetchProduct(product.handle).then(function (fresh) {
        if (!fresh || !fresh.available) {
          if (card.parentNode) card.parentNode.removeChild(card);
          if (!list.children.length && wrap.parentNode) wrap.parentNode.removeChild(wrap);
          return;
        }
        price.textContent = formatMoney(fresh.price, true);
      });
    });

    wrap.appendChild(list);
    return wrap;
  }

  function renderExtras() {
    if (!state.mounted) return;
    var inCartProducts = {};
    var inCartVariants = {};
    if (state.cart) {
      state.cart.items.forEach(function (item) {
        inCartProducts[item.product_id] = true;
        inCartVariants[item.variant_id] = true;
      });
    }

    var forgot = currentForgot(inCartVariants);
    var recent = recentProducts(inCartProducts, forgot);
    // Rebuild only when the content changes, so the row keeps its scroll position.
    var signature =
      (forgot ? forgot.variant_id : '') +
      '|' +
      recent
        .map(function (product) {
          return product.id;
        })
        .join(',');
    if (signature === state.extrasSignature) return;
    state.extrasSignature = signature;

    els.extras.textContent = '';
    if (forgot) els.extras.appendChild(buildForgot(forgot));
    if (recent.length) els.extras.appendChild(buildRecent(recent));
    els.extras.hidden = !els.extras.children.length;
  }

  /* ---------- wiring ---------- */

  function cartSignature(cart) {
    return (
      cartToken(cart) +
      '|' +
      cart.total_price +
      '|' +
      (state.giftBlocked ? 1 : 0) +
      '|' +
      cart.items
        .map(function (item) {
          return item.key + ':' + item.quantity + ':' + item.final_line_price;
        })
        .join(',')
    );
  }

  // startedAt is set for plain cart reads (when the request was sent) and left
  // out for carts that come from a change (add, quantity, removal, theme event).
  function onCart(cart, fromEvent, startedAt) {
    if (!cart || !Array.isArray(cart.items)) return;
    // A gift add/remove is in flight; it re-syncs when it finishes.
    if (state.busy) return;
    if (startedAt != null) {
      // A read that was sent before a newer read or before the last change
      // may describe an older cart: never let it overwrite newer data.
      if (startedAt < state.lastReadStart) return;
      if (startedAt < state.lastChangeAt) {
        scheduleSync();
        return;
      }
      state.lastReadStart = startedAt;
    }
    var mounted = mount();
    // The same cart arrives several times (theme, apps, own checks): draw once.
    var signature = cartSignature(cart);
    if (signature === state.signature) return;
    if (startedAt == null) state.lastChangeAt = now();
    state.signature = signature;
    state.cart = cart;
    track(cart, !!(fromEvent && state.ready));
    if (!mounted) return;
    els.cross.hidden = !state.hasCross || !cart.item_count;
    renderRewards(cart);
    renderExtras();
    syncGift(cart);
  }

  // Reads the cart from the server. At most two reads run at a time; further
  // calls are folded into one follow-up read.
  function syncNow() {
    if (state.reading >= 2) {
      state.readAgain = true;
      return;
    }
    state.reading += 1;
    var startedAt = now();
    fetchCart()
      .then(function (cart) {
        onCart(cart, true, startedAt);
      })
      .catch(function () {})
      .then(function () {
        state.reading -= 1;
        if (state.readAgain) {
          state.readAgain = false;
          syncNow();
        }
      });
  }

  // Confirms the cart shortly after a change. A burst of events costs one request.
  function scheduleSync() {
    if (state.syncTimer) clearTimeout(state.syncTimer);
    state.syncTimer = setTimeout(function () {
      state.syncTimer = null;
      syncNow();
    }, 400);
  }

  // Applies lines returned by an add-to-cart response to the last known cart,
  // so the bar moves at once; the confirming read follows.
  function applyAdded(lines) {
    if (!state.cart || !Array.isArray(lines) || !lines.length) {
      syncNow();
      return;
    }
    var next = {};
    Object.keys(state.cart).forEach(function (key) {
      next[key] = state.cart[key];
    });
    next.items = state.cart.items.slice();
    var usable = true;
    lines.forEach(function (line) {
      if (!line || !line.key || typeof line.final_line_price !== 'number') {
        usable = false;
        return;
      }
      var index = -1;
      next.items.forEach(function (item, i) {
        if (item.key === line.key) index = i;
      });
      if (index > -1) {
        next.total_price += line.final_line_price - next.items[index].final_line_price;
        next.items[index] = line;
      } else {
        next.total_price += line.final_line_price;
        next.items.unshift(line);
      }
    });
    if (usable) {
      next.item_count = next.items.reduce(function (count, item) {
        return count + item.quantity;
      }, 0);
      onCart(next, true);
    }
    scheduleSync();
  }

  /* ---------- reading the cart responses the page already gets ---------- */

  // 'read' for /cart.js, 'add' for /cart/add.js, 'change' for the other cart writes.
  function cartEndpoint(url, method) {
    var address = String(url || '');
    // Only this shop's own cart endpoints count.
    if (/^(https?:)?\/\//i.test(address) && address.replace(/^https?:/i, '').indexOf('//' + location.host + '/') !== 0) return null;
    var match = /\/cart(?:\.js|\.json|\/(add|change|update|clear)(?:\.js)?)(?:[?#]|$)/.exec(address);
    if (!match) return null;
    if (match[1] === 'add') return 'add';
    if (match[1]) return 'change';
    return String(method || 'GET').toUpperCase() === 'GET' ? 'read' : 'change';
  }

  function fromNetwork(kind, data, startedAt) {
    if (!data || typeof data !== 'object') return;
    if (Array.isArray(data.items) && typeof data.total_price === 'number') {
      onCart(data, true, kind === 'read' ? startedAt : null);
    } else if (kind === 'add') {
      applyAdded(Array.isArray(data.items) ? data.items : [data]);
    }
  }

  // Passive: requests and responses are passed through untouched.
  function tapNetwork() {
    try {
      var open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          var kind = cartEndpoint(url, method);
          if (kind) {
            var startedAt = now();
            this.addEventListener('load', function () {
              try {
                if (this.status < 200 || this.status >= 300) return;
                fromNetwork(kind, this.responseType === 'json' ? this.response : JSON.parse(this.responseText), startedAt);
              } catch (e) {}
            });
          }
        } catch (e) {}
        return open.apply(this, arguments);
      };
    } catch (e) {}

    if (typeof nativeFetch !== 'function') return;
    try {
      window.fetch = function (input, init) {
        var promise = nativeFetch.apply(window, arguments);
        try {
          var kind = cartEndpoint(
            typeof input === 'string' ? input : (input && (input.url || input.href)) || '',
            (init && init.method) || (input && input.method)
          );
          if (kind) {
            var startedAt = now();
            promise
              .then(function (res) {
                if (!res || !res.ok) return;
                return res
                  .clone()
                  .json()
                  .then(function (data) {
                    fromNetwork(kind, data, startedAt);
                  });
              })
              .catch(function () {});
          }
        } catch (e) {}
        return promise;
      };
    } catch (e) {}
  }

  // Runs whenever the theme re-renders the line items: the bar must never
  // disagree with what the drawer shows, even before the server has answered.
  function reconcile() {
    if (!state.mounted || state.busy) return;
    var rendered = els.items.querySelectorAll('.cart__item').length;
    var known = state.cart ? state.cart.items.length : 0;
    if (!rendered) {
      var emptied = !els.items.children.length || !!els.items.querySelector('.quick-cart__empty-state');
      if (emptied) {
        els.rewards.hidden = true;
        els.cross.hidden = true;
        state.signature = null;
        if (known) {
          // The drawer is ahead of the last known cart: trust the drawer.
          var emptiedCart = {};
          Object.keys(state.cart).forEach(function (key) {
            emptiedCart[key] = state.cart[key];
          });
          emptiedCart.items = [];
          emptiedCart.item_count = 0;
          emptiedCart.total_price = 0;
          state.cart = emptiedCart;
          known = 0;
          syncNow();
        }
      }
    } else if (rendered === known) {
      renderRewards(state.cart);
      els.cross.hidden = !state.hasCross;
    }
    if (rendered !== known) syncNow();
  }

  function init() {
    mount();
    var startedAt = now();
    fetchCart()
      .then(function (cart) {
        onCart(cart, false, startedAt);
      })
      .catch(function () {})
      .then(function () {
        state.ready = true;
      });

    document.addEventListener('flu:cart:updated', function (event) {
      var cart = event.detail && event.detail.cart;
      if (cart && Array.isArray(cart.items)) {
        onCart(cart, true);
      } else {
        syncNow();
      }
    });
    // Adding to cart fires its own event with the added line, not the cart.
    document.addEventListener('flu:cart:item-added', function (event) {
      var line = event.detail && event.detail.product;
      if (line && line.key) {
        applyAdded(Array.isArray(line.items) ? line.items : [line]);
      } else {
        syncNow();
      }
    });
    document.addEventListener('flu:quick-cart:open', function (event) {
      // The theme's copy of the cart is only used until the first read is back.
      var cart = event.detail && event.detail.cart;
      if (!state.cart && cart && Array.isArray(cart.items) && typeof cart.total_price === 'number') onCart(cart, true);
      if (mount()) renderExtras();
      scheduleSync();
    });
    // Back/forward cache: the page is shown again with a stale bar.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) syncNow();
    });
  }

  tapNetwork();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.NovaCart = {
    config: cfg,
    refresh: function () {
      state.signature = null;
      var startedAt = now();
      return fetchCart().then(function (cart) {
        onCart(cart, false, startedAt);
      });
    },
    // Support helper: a snapshot of what the script currently believes.
    debug: function () {
      return {
        mounted: state.mounted,
        busy: state.busy,
        giftBlocked: state.giftBlocked,
        barHidden: els.rewards ? els.rewards.hidden : null,
        message: els.message ? els.message.textContent : null,
        cartLines: state.cart ? state.cart.items.length : null,
        cartTotal: state.cart ? state.cart.total_price : null
      };
    }
  };
})();